import { useCallback, useMemo, useReducer, useState } from 'react';

import { useViewingRelay, ViewingRelayProvider } from 'my-core/hooks';
import useOnlineCourseBookletPrint from 'my-core/hooks/useOnlineCourseBookletPrint';
import { useActiveComponent } from 'my-core/in-course-experience-activity-hooks';
import {
  canPreviewOnlineCourse,
  canViewOnlineCourseActivity,
  hasActivityTokenPurchase,
  isPracticeModeEnabled,
  isPreviewableActivity,
  isSubscriptionOffering,
} from 'my-core/online-course-utils';
import { onlineCourseEditPath } from 'my-core/routes';

import InactivityWatcher from 'my-components/InactivityWatcher';
import ActivityBlocked from 'my-components/OnlineCourse/InCourseExperience/ActivityBlocked';
import ActivityError from 'my-components/OnlineCourse/InCourseExperience/ActivityError';
import CourseAssessment from 'my-components/OnlineCourse/InCourseExperience/CourseAssessment';
import InCourseExperienceChapterCover from 'my-components/OnlineCourse/InCourseExperience/InCourseExperienceChapterCover';
import InCourseExperienceHeader from 'my-components/OnlineCourse/InCourseExperience/InCourseExperienceHeader';
import InCourseExperienceSectionCover from 'my-components/OnlineCourse/InCourseExperience/InCourseExperienceSectionCover';
import OnlineCourseLesson from 'my-components/OnlineCourseLesson';
import PracticeQuestion from 'my-components/OnlineCoursePracticeQuestion';
import SubscribeCTABanner from 'my-components/SubscribeCTABanner';

import GradeBoosterBanner from './GradeBoosterBanner';

export default function StudentCoursePageActivity({
  activeComponent,
  activityTitle,
  assessment,
  basePath,
  canViewAll,
  course,
  courseSubscription,
  disabled,
  drawerOpen,
  onlineCourse,
  onlineCourseUser,
  onPurchase,
  onSaveProgress,
  onToggleDrawer,
  onTokenPurchase,
  progress,
  studySessions,
  subscription,
  tokenBalance,
  tokenPurchases,
  tokenPurchaseStatus,
  userCourse,
}) {
  const [playingState, togglePlaying] = useReducer(t => !t, false);
  const playing = !disabled && playingState;
  const [inactive, setInactive] = useState(false);
  const viewing = useViewingRelay();

  const reviewMode = false; // TODO
  const autoplay = false; // TODO

  const canViewActivity = canViewOnlineCourseActivity(activeComponent, {
    canPreviewAll: canPreviewOnlineCourse(onlineCourse, canViewAll),
    canViewAll,
    tokenPurchases,
  });
  const {
    activity,
    chapter,
    eventTrackingProperties,
    fetchActivityStatus,
    fetchingActivity,
    goToNext,
    handleActivityCompleted,
    nextAndPrev,
    privateContent,
    saveActivityProgress,
    section,
    tokenPurchase,
  } = useActiveComponent({
    activeComponent,
    assessmentId: assessment?.id,
    basePath,
    canViewActivity,
    course,
    onTokenPurchase,
    onSaveProgress,
    inactive,
    disabled,
    onlineCourse,
    progress,
    tokenPurchaseStatus,
  });

  const { data: ocActivity } = activeComponent;
  const activityType = ocActivity?.activity_type;
  const isSubscriptionOc = isSubscriptionOffering(onlineCourse);

  // TODO: add print options dialog
  const [printBooklet, fetchingBooklet] = useOnlineCourseBookletPrint();
  const printCurrentChapter = useMemo(
    () => chapter?.fullPath && (() => printBooklet(onlineCourse.id, chapter.fullPath)),
    [printBooklet, chapter?.fullPath, onlineCourse.id],
  );

  const favorited = !!progress?.starred;
  const toggleFavorited = useCallback(
    starred => {
      onSaveProgress(ocActivity.id, { starred }, { immediate: true });
    },
    [ocActivity.id, onSaveProgress],
  );

  return (
    <>
      <InCourseExperienceHeader
        activeComponent={activeComponent}
        activityFavorited={favorited}
        canShare={!onlineCourse.in_course_settings.sharing_disabled && canViewActivity}
        drawerOpen={drawerOpen}
        editPath={onlineCourseEditPath(onlineCourse, {
          sect_id: section?.id,
          chap_id: chapter?.id,
          act_id: ocActivity?.id,
        })}
        nextAndPrev={nextAndPrev}
        onlineCourse={onlineCourse}
        onToggleDrawer={onToggleDrawer}
        onToggleFavorite={!!onlineCourseUser && toggleFavorited}
        practiceModePath={
          section && isPracticeModeEnabled(onlineCourse) && `${basePath}/practice-mode${section.fullPath}`
        }
        title={activityTitle}
      />
      {isSubscriptionOc && (
        <SubscribeCTABanner
          actionText={activityType === 'QuizQuestion' ? 'Check Your Answer' : 'Watch Video Lessons'}
          freeActivity={activeComponent.type === 'activity' && isPreviewableActivity(activeComponent)}
          hidden={canViewAll}
          inCourse
          tokenPurchased={!canViewAll && hasActivityTokenPurchase(activeComponent, tokenPurchases)}
        />
      )}
      {!!studySessions?.length && (
        <GradeBoosterBanner assessment={assessment} course={course} studySessions={studySessions} />
      )}
      <ViewingRelayProvider value={viewing}>{renderActivity()}</ViewingRelayProvider>
      <InactivityWatcher disabled={playing || disabled} onToggleInactive={setInactive} />
    </>
  );

  function renderActivity() {
    if (activeComponent.type === 'chapter') {
      return <InCourseExperienceChapterCover activeComponent={activeComponent} basePath={basePath} />;
    }
    if (activeComponent.type === 'section') {
      return <InCourseExperienceSectionCover activeComponent={activeComponent} basePath={basePath} />;
    }

    if (privateContent) {
      return (
        <ActivityBlocked
          courseSubscription={courseSubscription}
          eventTrackingProperties={eventTrackingProperties}
          onlineCourse={onlineCourse}
          onlineCourseUser={onlineCourseUser}
          onPurchase={onPurchase}
          onTokenPurchase={tokenPurchase}
          subscription={subscription}
          tokenPurchaseStatus={tokenPurchaseStatus}
          userCourse={userCourse}
        />
      );
    }

    if (fetchActivityStatus?.error) {
      return (
        <ActivityError drawerOpen={drawerOpen} error={fetchActivityStatus.error} onToggleDrawer={onToggleDrawer} />
      );
    }

    const activityProps = {
      canView: canViewActivity,
      courseSubscription,
      eventTrackingProperties,
      fetching: fetchingActivity,
      onlineCourse,
      onlineCourseUser,
      onCompleted: handleActivityCompleted,
      onPurchase,
      onSaveProgress: saveActivityProgress,
      // onShowFeedback,
      onTokenPurchase: tokenPurchase,
      onViewingChange: viewing.reportViewing,
      reviewMode,
      subscription,
      // subscriptionTrialStatus,
      tokenBalance: isSubscriptionOc ? tokenBalance : 0,
      tokenPurchaseStatus,
      userCourse,
    };

    if (activityType === 'Quiz') {
      return (
        <CourseAssessment
          key={ocActivity.id}
          courseSlug={course?.slug}
          isFinalExam={ocActivity.activity_metadata?.quiz_type === 'practice_exam'}
          quiz={activity}
          quizQuestionSkipDisabled={onlineCourse.in_course_settings.skip_question_disabled || ocActivity.disable_skip}
          title={ocActivity.title}
          {...activityProps}
        />
      );
    }

    const extraActivityProps = {
      onTogglePlaying: togglePlaying,
      playing,
      progress, // video play start / is_completed
      videoDrmId: `OCA:${ocActivity.id}`,
    };

    if (activityType === 'CourseLesson') {
      return (
        <OnlineCourseLesson
          key={ocActivity.id}
          activityTitle={ocActivity.title || ''}
          autoplay={autoplay}
          courseLesson={activity}
          fetchingBooklet={fetchingBooklet}
          onGoToNext={goToNext || undefined}
          onPrintBooklet={printCurrentChapter}
          {...activityProps}
          {...extraActivityProps}
        />
      );
    }
    return (
      <PracticeQuestion
        key={ocActivity.id}
        {...activityProps}
        {...extraActivityProps}
        quizQuestion={activity}
        quizQuestionSkipDisabled={onlineCourse.in_course_settings.skip_question_disabled || ocActivity.disable_skip}
        quizQuestionType={ocActivity.activity_metadata?.question_type}
      />
    );
  }
}
